import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import MUICssBaseline from "@mui/material/CssBaseline";
import MUIThemeProvider from "@mui/material/styles/ThemeProvider";
import Setting from "../Utility/Setting";
import { createSalesPilotTheme } from "./Theme";

/**
 * Provides the Sales Pilot Material UI theme for the entire application and
 * re-creates the theme when the user changes light/dark mode in settings.
 * @param {object} props
 * @param {React.ReactNode} props.children - Application tree to render inside the theme.
 * @returns {React.ReactElement}
 */
export default function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(
    Setting.get("ui.theme") || "light",
  );
  const currentLocation = useLocation();

  useEffect(() => {
    const handleThemeSettingChange = () => {
      setThemeName(Setting.get("ui.theme") || "light");
    };

    window.addEventListener("setting.ui.theme", handleThemeSettingChange);
    return () =>
      window.removeEventListener("setting.ui.theme", handleThemeSettingChange);
  }, []);

  const effectiveThemeName =
    currentLocation.pathname.startsWith("/patientInformation") ||
    currentLocation.pathname.startsWith("/reports/home") ||
    currentLocation.pathname.startsWith("/productType/new") ||
    currentLocation.pathname.startsWith("/stateSalesTax") ||
    currentLocation.pathname.startsWith("/account")
      ? "light"
      : themeName;

  const theme = useMemo(
    () => createSalesPilotTheme(effectiveThemeName),
    [effectiveThemeName],
  );

  return (
    <MUIThemeProvider theme={theme}>
      <MUICssBaseline />
      {children}
    </MUIThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
